import { useMemo } from 'react';

import { Line, LineChart, XAxis, YAxis } from 'recharts';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore library installed on parent module, overriding tsc check
import { Payload } from 'recharts/types/component/DefaultTooltipContent';

import {
  ChartConfig,
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from 'components/Chart';
import { Flex } from 'components/layout';
import { Typography } from 'components/typography';
import {
  SuperDispatchTrendPoint,
  SuperDispatchVehicleTypePriceTrend,
} from 'lib/api/getQuickQuote';
import { useQuickQuoteCurrency } from 'pages/QuoteView/Quoting/RequestQuickQuote/hooks/useQuickQuoteCurrency';
import { Maybe } from 'types/UtilityTypes';
import { cn } from 'utils/shadcn';

import {
  SuperDispatchTrendChartSeries,
  buildSuperDispatchTrendChartData,
  buildSuperDispatchTrendXTickIndexes,
} from './buildSuperDispatchTrendChartData';
import { formatLaneDate } from './laneRecordFormatting';
import { superDispatchPriceTrendChartConfig } from './superDispatchPriceTrendChartConfig';

type SuperDispatchPriceTrendChartProps = {
  priceTrend: Maybe<
    SuperDispatchVehicleTypePriceTrend[] | SuperDispatchTrendPoint[]
  >;
  formatAmount: ReturnType<typeof useQuickQuoteCurrency>['formatAmount'];
};

type SuperDispatchTrendTooltipProps = {
  active?: boolean;
  payload?: ReadonlyArray<Payload<number, string>>;
  activeIndex?: Maybe<number | string>;
  pointDates: string[];
  series: SuperDispatchTrendChartSeries[];
  formatAmount: SuperDispatchPriceTrendChartProps['formatAmount'];
};

function trendYDomain(max: number): [number, number] {
  const padding = Math.max(max * 0.08, 1);
  return [0, max + padding];
}

function buildYAxisTicks(dataMax: number): number[] {
  return [0, dataMax];
}

function seriesLabel(
  series: SuperDispatchTrendChartSeries[],
  dataKey: string
): string {
  const match = series.find((item) => item.key === dataKey);
  if (match) {
    return match.label;
  }

  return String(superDispatchPriceTrendChartConfig.price.label ?? 'Price');
}

function SuperDispatchTrendTooltip({
  active,
  payload,
  activeIndex,
  pointDates,
  series,
  formatAmount,
}: SuperDispatchTrendTooltipProps) {
  if (!active || !payload?.length) {
    return null;
  }

  const visibleRows = payload.filter(
    (entry) => entry.value != null && !Number.isNaN(Number(entry.value))
  );
  if (visibleRows.length === 0) {
    return null;
  }

  const pointDate = visibleRows[0]?.payload?.date;
  const payloadIndex =
    typeof pointDate === 'string' ? pointDates.indexOf(pointDate) : -1;
  const parsedActiveIndex =
    activeIndex == null ? Number.NaN : Number(activeIndex);
  const pointIndex = Number.isFinite(parsedActiveIndex)
    ? parsedActiveIndex
    : payloadIndex;
  const showOnLeft =
    pointIndex >= 0 && pointIndex >= (pointDates.length - 1) / 2;

  return (
    <ChartTooltipContent
      active={active}
      payload={[...visibleRows]}
      hideLabel
      hideIndicator
      className={cn(
        'w-max min-w-[7.5rem] max-w-[11rem]',
        showOnLeft ? '-translate-x-full -mr-2' : 'ml-2'
      )}
      formatter={(
        value: any,
        name: any,
        row: Payload<any, any>,
        index: number
      ) => (
        <>
          {index === 0 && (
            <Typography variant='body-xxs' weight='medium' className='w-full'>
              {formatLaneDate(row.payload.date)}
            </Typography>
          )}
          <Flex justify='between' align='center' gap='sm' className='w-full'>
            <Typography
              as='span'
              variant='body-xxs'
              className='text-neutral-500'
            >
              {seriesLabel(series, String(name))}
            </Typography>
            <Typography
              as='span'
              variant='body-xxs'
              weight='medium'
              className='font-mono tabular-nums text-neutral-500'
            >
              {formatAmount(Number(value))}
            </Typography>
          </Flex>
        </>
      )}
    />
  );
}

// Renders one line per vehicle type (per-unit medians from the API).
export function SuperDispatchPriceTrendChart({
  priceTrend,
  formatAmount,
}: SuperDispatchPriceTrendChartProps) {
  const { rows: chartData, series } = useMemo(
    () => buildSuperDispatchTrendChartData(priceTrend),
    [priceTrend]
  );

  const chartConfig = useMemo(() => {
    const config: ChartConfig = {
      price: superDispatchPriceTrendChartConfig.price,
    };
    for (const item of series) {
      config[item.key] = {
        label: item.label,
        color: superDispatchPriceTrendChartConfig.price.color,
      };
    }
    return config;
  }, [series]);

  if (series.length === 0 || chartData.length === 0) {
    return null;
  }

  const values = chartData.flatMap((row) =>
    series
      .map((item) => row[item.key])
      .filter(
        (price): price is number => typeof price === 'number' && price > 0
      )
  );
  if (values.length === 0) {
    return null;
  }

  const max = Math.max(...values);
  const yDomain = trendYDomain(max);
  const yTicks = buildYAxisTicks(max);
  const yAxisLabelSample = formatAmount(max);
  const yAxisWidth = Math.min(
    52,
    Math.max(36, yAxisLabelSample.length * 5.5 + 8)
  );
  const xTickIndexes = buildSuperDispatchTrendXTickIndexes(chartData.length);

  return (
    <div className='w-full min-w-0'>
      <Typography
        variant='body-xxs'
        weight='medium'
        className='mb-1 text-neutral-600'
      >
        Price trend
      </Typography>
      <ChartContainer
        config={chartConfig}
        className='aspect-auto h-24 w-full min-h-24 justify-start [&_.recharts-responsive-container]:!h-full'
        role='img'
        aria-label={`Recommended price trend from ${formatAmount(0)} to ${formatAmount(max)}`}
      >
        <LineChart
          data={chartData}
          margin={{ top: 4, right: 4, bottom: 0, left: 0 }}
        >
          <YAxis
            orientation='left'
            domain={yDomain}
            ticks={yTicks}
            allowDecimals={false}
            tickFormatter={(value) => formatAmount(Number(value))}
            tickLine={false}
            axisLine={false}
            width={yAxisWidth}
            tick={{ fontSize: 10 }}
          />
          <XAxis
            dataKey='index'
            type='number'
            domain={['dataMin', 'dataMax']}
            ticks={xTickIndexes}
            tickLine={false}
            axisLine={{ stroke: '#e5e5e5' }}
            tickFormatter={(index) => {
              const row = chartData[Number(index)];
              return row?.date ? formatLaneDate(row.date) : '';
            }}
            height={22}
            tick={{ fontSize: 10 }}
          />
          <ChartTooltip
            allowEscapeViewBox={{ x: true, y: false }}
            wrapperStyle={{ zIndex: 10, pointerEvents: 'none' }}
            cursor={false}
            offset={8}
            content={(tooltipProps) => (
              <SuperDispatchTrendTooltip
                active={tooltipProps.active}
                payload={
                  tooltipProps.payload as ReadonlyArray<Payload<number, string>>
                }
                activeIndex={tooltipProps.activeIndex}
                pointDates={chartData.map((point) => point.date)}
                series={series}
                formatAmount={formatAmount}
              />
            )}
          />
          {series.map((item) => (
            <Line
              key={item.key}
              type='monotone'
              dataKey={item.key}
              name={item.key}
              stroke='var(--color-price)'
              strokeWidth={2}
              connectNulls
              dot={{ r: 2, strokeWidth: 0 }}
              activeDot={{ r: 3, strokeWidth: 0 }}
              isAnimationActive={false}
            />
          ))}
        </LineChart>
      </ChartContainer>
    </div>
  );
}
